import { asyncScheduler, from, Observable, of, scheduled, zip } from 'rxjs';
import { filter, map, mergeMap, reduce, tap } from 'rxjs/operators';

import {
  IProcessedValidationConfig,
  jsonataEval,
  ValidatorFunction,
  validators,
} from '@assess/shared/dynamic-components';
import { logger } from '@assess/shared/logger';

import { getControl } from './get-control';
import { Control } from '../types/control.type';
import { DynamicValidationSummary, DynamicValidationSummaryEntry } from '../types/dynamic-validation-summary.type';

export const validateForm = (
  form: Control,
  processedConfigs: Map<string, IProcessedValidationConfig>
): Observable<any> => {
  return scheduled(from(processedConfigs), asyncScheduler).pipe(
    map(([targetPath, processedConfig]) => ({
      targetPath,
      processedConfig,
      control: getControl(form, targetPath),
    })),
    filter(({ control, targetPath }) => {
      const valid = !!control;
      if (!valid) {
        logger.warn({ context: 'validateForm', data: { targetPath } }, 'Missing control');
      }

      return valid;
    }),
    mergeMap(({ control, targetPath, processedConfig }) =>
      zip(of(targetPath), validateControl(form.value, control as Control, processedConfig))
    ),
    reduce((acc, [key, errors]) => {
      Object.keys(errors ?? {}).length ? acc.set(key, errors) : acc.delete(key);

      return acc;
    }, new Map() as DynamicValidationSummary)
  );
};

const validateControl = (value: any, control: Control, processedConfig: IProcessedValidationConfig) => {
  return scheduled(from(processedConfig.validators), asyncScheduler).pipe(
    filter(([validatorKey]) => validators.has(validatorKey)),
    mergeMap(async ([validatorKey, validatorArgs]) => {
      if (validatorArgs.expression) {
        const result = (await jsonataEval(validatorArgs.expression, value)) === true;

        return { validatorKey, validatorArgs, result };
      }

      return { validatorKey, validatorArgs, result: true };
    }),
    filter(({ result }) => result),
    mergeMap(async ({ validatorKey, validatorArgs }) => {
      const validatorFunction = validators.get(validatorKey) as ValidatorFunction;
      const { args, message } = validatorArgs;

      let validationResult = validatorFunction(control.value, args, value);

      if (validationResult instanceof Promise) {
        validationResult = await validationResult;
      }

      if (!validationResult) {
        return {};
      }

      const [[resultKey, resultValue]] = Object.entries(validationResult);

      return {
        [resultKey]: { ...resultValue, message: message || resultValue?.message },
      } as DynamicValidationSummaryEntry;
    }),
    reduce((acc, current) => ({ ...acc, ...current }), {} as Record<string, DynamicValidationSummaryEntry>),
    map((errors) => (Object.keys(errors).length > 0 ? errors : null)),
    tap((errors) => control.setErrors(errors))
  );
};
